/**
 * Where everything sits in the week grid.
 *
 * Kept out of the component and free of the DOM so it can be tested with
 * numbers: a chip in the wrong column or half an hour adrift is the kind of
 * fault that reads as "the calendar is broken" and cannot be seen in a
 * snapshot.
 *
 * Every date is read through `parseSessionDate`, never `new Date(string)`:
 * `start_date` is a varchar of naked hub-local wall clock with no zone, and
 * the browser would read it as UTC and slide the whole grid by the reader's
 * offset.
 */
import { parseSessionDate } from './sessionCard';
import { dateRange } from './usDate';

/** The board's window. Widened by the week's own events when they fall outside. */
export const DEFAULT_FIRST_HOUR = 9;
export const DEFAULT_LAST_HOUR = 20;

/** One hour of grid. The board draws 46px. */
export const HOUR_HEIGHT = 46;

/** Below this a chip cannot hold its own title, so it stops shrinking. */
const MIN_EVENT_HEIGHT = 22;

export const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const midnight = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

/** Monday of the week the given date falls in. Weeks start on Monday here. */
export function startOfWeek(date) {
  const day = midnight(date);
  // getDay() is Sunday-first; Sunday belongs to the week that just ended.
  const shift = (day.getDay() + 6) % 7;

  day.setDate(day.getDate() - shift);

  return day;
}

/** The seven days of that week, Monday first. */
export function weekDays(anchor) {
  const monday = startOfWeek(anchor);

  return Array.from({ length: 7 }, (_, index) => {
    const day = new Date(monday);
    day.setDate(monday.getDate() + index);

    return day;
  });
}

export const isSameDay = (a, b) =>
  a instanceof Date && b instanceof Date
  && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** The hour, with its minutes as a fraction — 18:30 is 18.5. */
export function asFractionalHour(date) {
  return date ? date.getHours() + date.getMinutes() / 60 : null;
}

/**
 * The hour a session ends, as drawn in its start's column: an end on a later
 * calendar day is the end of that column, not an early hour of the next.
 */
// ponytail: the part after midnight is not drawn in the next day's column.
const endHourOf = (start, end) => (isSameDay(start, end) ? asFractionalHour(end) : 24);

/**
 * The hours the grid draws.
 *
 * Nine to eight is the board's default, but a 7am session that fell outside
 * the window used to be invisible rather than early — so the range stretches
 * to hold whatever the week actually contains.
 */
export function hourRange(sessions = []) {
  let first = DEFAULT_FIRST_HOUR;
  let last = DEFAULT_LAST_HOUR;

  for (const session of sessions) {
    const start = parseSessionDate(session?.start_date);
    const end = parseSessionDate(session?.end_date);

    if (start) first = Math.min(first, Math.floor(asFractionalHour(start)));
    if (start && end) last = Math.max(last, Math.ceil(endHourOf(start, end)));
  }

  return { first, last: Math.max(last, first + 1) };
}

export const hoursIn = ({ first, last }) =>
  Array.from({ length: Math.max(0, last - first) }, (_, index) => first + index);

/**
 * A chip's offset and height in pixels, against a given hour range.
 *
 * A session that runs past the end of the grid is cut off at it rather than
 * drawn through the bottom of the card; one with no end gets the minimum,
 * which reads as "a moment" rather than as all day.
 */
export function eventGeometry(session, range, hourHeight = HOUR_HEIGHT) {
  const start = parseSessionDate(session?.start_date);

  if (!start) return null;

  const end = parseSessionDate(session?.end_date);
  const startHour = asFractionalHour(start);
  const endHour = end ? endHourOf(start, end) : startHour;

  const top = (startHour - range.first) * hourHeight;
  const bottom = Math.min(endHour, range.last) * hourHeight - range.first * hourHeight;

  return {
    top: Math.max(0, top),
    height: Math.max(MIN_EVENT_HEIGHT, bottom - Math.max(0, top)),
  };
}

/**
 * Where the now-line sits, or null when the current time is outside the hours
 * on screen — a line pinned to the top of a grid that starts at nine would say
 * it is nine in the morning at six.
 */
export function nowOffset(range, now = new Date(), hourHeight = HOUR_HEIGHT) {
  const hour = asFractionalHour(now);

  if (hour < range.first || hour > range.last) return null;

  return (hour - range.first) * hourHeight;
}

/** Which of the seven columns a session belongs in, or -1 when it is elsewhere. */
export function columnFor(session, days) {
  const start = parseSessionDate(session?.start_date);

  if (!start) return -1;

  return days.findIndex((day) => isSameDay(day, start));
}

/** "August 31 – September 6, 2026", the heading over the grid. */
export function weekLabel(anchor) {
  const days = weekDays(anchor);
  return dateRange(days[0], days[6]);
}

/** The window to ask the agenda for, as the API's two date-only bounds. */
export function weekWindow(anchor) {
  const days = weekDays(anchor);
  const iso = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

  return { from: iso(days[0]), to: iso(days[6]) };
}

export const shiftWeeks = (anchor, weeks) => {
  const moved = new Date(anchor);
  moved.setDate(moved.getDate() + weeks * 7);

  return moved;
};
